import { mapGetters, mapMutations } from "vuex";

export default {
  computed: {
    ...mapGetters(["getPageState", "isPageStateValid"]),
  },

  methods: {
    ...mapMutations(["savePageState", "clearPageState", "updatePageState"]),

    /**
     * 保存页面状态
     * @param {string} pageName - 页面名称，建议使用路由name
     * @param {object} data - 要保存的数据
     */
    saveCurrentPageState(data, customPageName) {
      const pageName = customPageName || this.getPageName();
      this.savePageState({ pageName, data });
    },

    /**
     * 获取页面状态
     * @param {string} pageName - 页面名称，默认使用当前路由
     * @returns {object|null} 页面状态数据
     */
    getCurrentPageState(pageName) {
      const name = pageName || this.getPageName();
      return this.getPageState(name);
    },

    /**
     * 检查页面状态是否有效
     * @param {string} pageName - 页面名称，默认使用当前路由
     * @param {number} maxAge - 最大有效期（毫秒），默认30分钟
     * @returns {boolean} 是否有效
     */
    isCurrentPageStateValid(pageName, maxAge) {
      const name = pageName || this.getPageName();
      return this.isPageStateValid(name, maxAge);
    },

    /**
     * 更新页面状态
     * @param {object} data - 要更新的数据
     * @param {string} pageName - 页面名称，默认使用当前路由
     */
    updateCurrentPageState(data, pageName) {
      const name = pageName || this.getPageName();
      this.updatePageState({ pageName: name, data });
    },

    /**
     * 清除页面状态
     * @param {string} pageName - 页面名称，默认使用当前路由
     */
    clearCurrentPageState(pageName) {
      const name = pageName || this.getPageName();
      this.clearPageState(name);
    },

    /**
     * 恢复页面状态
     * @param {string} pageName - 页面名称，默认使用当前路由
     * @param {boolean} clearAfterRestore - 恢复后是否清除状态，默认false
     * @returns {boolean} 是否成功恢复
     */
    restorePageState(pageName, clearAfterRestore = false) {
      const name = pageName || this.getPageName();
      const state = this.getPageState(name);

      if (state && this.isPageStateValid(name)) {
        // 恢复数据到组件
        this.restoreDataFromState(state);

        if (clearAfterRestore) {
          this.clearPageState(name);
        }

        return true;
      }

      return false;
    },

    /**
     * 从状态恢复数据到组件
     * 子组件需要重写此方法来实现具体的数据恢复逻辑
     * @param {object} state - 页面状态数据
     */
    restoreDataFromState(state) {
      // 默认实现：将状态数据合并到组件数据中
      if (state && typeof state === "object") {
        Object.keys(state).forEach((key) => {
          if (
            key !== "timestamp" &&
            Object.prototype.hasOwnProperty.call(this.$data, key)
          ) {
            this.$data[key] = state[key];
          }
        });
      }
    },

    /**
     * 检查是否应该恢复页面状态
     * 子组件可以重写此方法来控制是否自动恢复状态
     * @returns {boolean} 是否应该恢复状态
     */
    shouldRestorePageState() {
      // 默认不自动恢复状态
      return false;
    },

    /**
     * 检查是否应该保存页面状态
     * 子组件可以重写此方法来控制是否自动保存状态
     * @returns {boolean} 是否应该保存状态
     */
    shouldSavePageState() {
      // 默认不自动保存状态
      return false;
    },

    /**
     * 获取页面名称
     * 子组件可以重写此方法来返回自定义的页面名称
     * @returns {string} 页面名称
     */
    getPageName() {
      // 默认使用路由名称或路径
      return this.$route.name || this.$route.path;
    },

    /**
     * 组件销毁前保存状态
     */
    savePageStateBeforeDestroy() {
      try {
        if (
          this.shouldSavePageState() &&
          typeof this.getDataToSave === "function"
        ) {
          const dataToSave = this.getDataToSave();
          if (dataToSave && Object.keys(dataToSave).length > 0) {
            this.saveCurrentPageState(dataToSave);
            console.log("状态已保存");
          }
        }
      } catch (error) {
        console.warn("保存页面状态时出错:", error);
      }
    },
  },

  /**
   * 组件激活时恢复状态（keep-alive模式）
   */
  activated() {
    // 如果使用keep-alive，在组件激活时恢复状态
    if (this.shouldRestorePageState()) {
      this.restorePageState();
      console.log("activated - keep-alive模式，状态已恢复");
    }
  },

  /**
   * 组件挂载时恢复状态（非keep-alive模式）
   */
  mounted() {
    // 组件挂载后尝试恢复状态
    if (this.shouldRestorePageState()) {
      this.restorePageState();
      console.log("mounted - 尝试恢复状态");
    }
  },

  /**
   * 组件销毁前保存状态
   */
  beforeDestroy() {
    // 组件销毁前保存当前状态
    this.savePageStateBeforeDestroy();
  },

  /**
   * 路由离开前保存状态
   */
  beforeRouteLeave(to, from, next) {
    // 路由离开前保存状态
    if (this.shouldSavePageState()) {
      this.savePageStateBeforeDestroy();
    }
    next();
  },

  /**
   * 路由进入后恢复状态
   */
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      // 路由进入后，在下一个tick中恢复状态
      vm.$nextTick(() => {
        if (vm.shouldRestorePageState()) {
          vm.restorePageState();
          console.log("beforeRouteEnter - 路由进入后恢复状态");
        }
      });
    });
  },

  /**
   * 获取需要保存的数据
   * 子组件需要重写此方法来指定要保存的数据
   * @returns {object} 要保存的数据
   */
  getDataToSave() {
    // 默认实现：返回组件的主要数据
    return {
      // 子组件可以重写此方法来返回具体需要保存的数据
      ...this.$data,
    };
  },
};
